import { Box, Button, Container, Typography, TextField, Grid, Paper, InputAdornment } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DealerList as FindDealerList, DealersMap as FindDealerMap, type DealersMapHandle } from '@/components/FindDealers/FindDealersIndex.ts';
import { ISearchDealerResult } from '@/types/ISearchDealerResult';
import { getLocation, searchDealers } from '@/api/searchDealers';
import './FindDealers.css';
import SearchIcon from '@mui/icons-material/Search';

const FindDealer = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [dealers, setDealers] = useState<ISearchDealerResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [currentSearch, setCurrentSearch] = useState('');
  const [currentCoordinates, setCurrentCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
  const [currentSortBy, setCurrentSortBy] = useState('distance');
  const [selectedDealerId, setSelectedDealerId] = useState<number | null>(null);
  const [shouldAutoCenterMap, setShouldAutoCenterMap] = useState(true);
  const [isLocationPermissionResolved, setIsLocationPermissionResolved] = useState(false);
  const hasInitialSearchRef = useRef(false);
  const isLocationRequested = useRef(false);
  const mapRef = useRef<DealersMapHandle>(null);

  const googleMapApiKey = import.meta.env.VITE_GOOGLE_MAP_API_KEY;
  useEffect(() => {
    if (isLocationRequested.current) return; 

    const requestLocationPermission = async () => {
      if ('geolocation' in navigator) {
        isLocationRequested.current = true;
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            setIsLocationPermissionResolved(true);
            const { latitude, longitude } = position.coords;
            setUserLocation({ lat: latitude, lng: longitude });

            if (!hasInitialSearchRef.current) {
              try {
                setIsLoading(true);
                mapRef.current?.setCenter(latitude, longitude);
                mapRef.current?.setZoom(9);
                const bounds = mapRef.current?.getCurrentBounds();
                const searchResults = await searchDealers('', { latitude, longitude, bounds: bounds }, 'distance');
                if (searchResults) {
                  setDealers(searchResults);
                  setCurrentCoordinates({ latitude, longitude });
                  setSelectedDealerId(null);
                  hasInitialSearchRef.current = true;
                }
              } catch (error) {
                console.error('Error searching installers:', error);
              } finally {
                setIsLoading(false);
              }
            }
          },
          async (error) => {
            setIsLocationPermissionResolved(true);
             await defaultLocation(setIsLoading, setDealers, setCurrentCoordinates, setSelectedDealerId, hasInitialSearchRef, mapRef);
            console.log('Location permission denied or error:', error.message);
          },
          {
            enableHighAccuracy: true,
            maximumAge: 0
          }
        );
      } else {
        setIsLocationPermissionResolved(true);
         await defaultLocation(setIsLoading, setDealers, setCurrentCoordinates, setSelectedDealerId, hasInitialSearchRef, mapRef);
      }
    };

    setTimeout(() => {
      requestLocationPermission();
    }, 500);
  }, []);
  return (
    <Container className="find-dealers-page-container" maxWidth="lg">
      <Paper elevation={3} className="find-dealers-page">
        <Grid container spacing={2} className="panel-wrapper">
          <Grid item xs={12} md={9} className="left-panel-wrapper">
            <Box
              className={`find-dealers-maps-container ${
                googleMapApiKey ? "with-api-key" : "no-api-key"
              }`}
            >
              <FindDealerMap
                ref={mapRef}
                apiKey={googleMapApiKey}
                dealers={dealers}
                userLocation={userLocation}
                selectedDealerId={selectedDealerId}
                shouldAutoCenter={shouldAutoCenterMap}
                isLocationPermissionResolved={isLocationPermissionResolved}
                onMapCenterChange={async (latitude: number, longitude: number, bounds: google.maps.LatLngBounds | undefined) => {
                  setShouldAutoCenterMap(false);
                  setIsLoading(true);
                  try {
                    const searchResults = await searchDealers('', { latitude, longitude, bounds }, currentSortBy, 200);
                    if (searchResults) {
                      setDealers(searchResults);
                      setCurrentSearch('');
                      setCurrentCoordinates({ latitude, longitude });
                      setSelectedDealerId(null);
                    }
                  } catch (error) {
                    console.error('Error searching installers:', error);
                  } finally {
                    setIsLoading(false);
                  }
                }}
              />
            </Box>
            <Box className="become-installer-box">
              <Typography
                className='become-installer-text'
                variant="h6"
              >
                Become a Trackmate Installer
              </Typography>
              <Box className="join-network-text">
                <Typography>Join our network of proffesionals and grow your business</Typography>
              </Box>
              <Button className="apply-btn" variant="contained"
                onClick={() => navigate('/resources/become-installer')}>
                Apply Here
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={3} className='right-panel-wrapper'>
            <Box className="top-box">
              <Typography variant="h6" className="find-installer-text">
                Find Certified Installers in your area
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter your city, zip code or address"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyPress={async (e) => {
                  if (e.key === 'Enter') {
                    setIsLoading(true);
                    try {
                      let searchResults;
                      const sortBy = currentSortBy || 'distance';
                      if (search.trim()) {
                        const location = await getLocation(search.trim())
                        mapRef.current?.setCenter(location.lat, location.lng);
                        mapRef.current?.setZoom(9);
                        const bounds = mapRef.current?.getCurrentBounds();
                        
                        searchResults = await searchDealers('', { 
                          latitude: location.lat, 
                          longitude: location.lng, 
                          bounds:  bounds
                        }, sortBy);
                        if (searchResults) {
                          setDealers(searchResults);
                          setCurrentSearch(search.trim());
                          setCurrentCoordinates(null);
                          setSelectedDealerId(null);
                        }
                      } else if (userLocation) {
                        mapRef.current?.setCenter(userLocation.lat, userLocation.lng);
                        mapRef.current?.setZoom(9);
                        const bounds = mapRef.current?.getCurrentBounds();
                        searchResults = await searchDealers('', { latitude: userLocation.lat, longitude: userLocation.lng, bounds }, sortBy);
                        if (searchResults) {
                          setDealers(searchResults);
                          setCurrentSearch('');
                          setCurrentCoordinates({ latitude: userLocation.lat, longitude: userLocation.lng });
                          setSelectedDealerId(null);
                        }
                      }
                    } catch (error) {
                      console.error('Error searching installers:', error);
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
                size="small"
                className="search-textbox"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ mt: 0.2, color: '#C4C4C4', fontSize: 24 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box className="bottom-box">
              <FindDealerList
                dealers={dealers}
                isLoading={isLoading}
                currentSearch={currentSearch}
                currentCoordinates={currentCoordinates}
                onDealerClick={(dealerId) => setSelectedDealerId(dealerId)}
                onSortChange={async (sortBy: string) => {
                  const sortByValue = sortBy || 'distance';
                  setCurrentSortBy(sortByValue);
                  setIsLoading(true);
                  try {
                    let searchResults;
                    if (currentSearch) {
                      const bounds = mapRef.current?.getCurrentBounds();
                      searchResults = await searchDealers(currentSearch, { 
                        latitude: mapRef.current?.getCurrentCenter()?.lat || 0, 
                        longitude: mapRef.current?.getCurrentCenter()?.lng || 0, 
                        bounds 
                      }, sortByValue);
                    } else if (currentCoordinates) {
                      const bounds = mapRef.current?.getCurrentBounds();
                      searchResults = await searchDealers('', { ...currentCoordinates, bounds }, sortByValue);
                    }
                    if (searchResults) {
                      setDealers(searchResults);
                      setSelectedDealerId(null);
                    }
                  } catch (error) {
                    console.error('Error searching installers with sort:', error);
                  } finally {
                    setIsLoading(false);
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

const defaultLocation = async (
  setIsLoading: (loading: boolean) => void,
  setDealers: (dealers: ISearchDealerResult[]) => void,
  setCurrentCoordinates: (coords: { latitude: number; longitude: number } | null) => void,
  setSelectedDealerId: (id: number | null) => void,
  hasInitialSearchRef: React.MutableRefObject<boolean>,
  mapRef: React.RefObject<DealersMapHandle>
) => {
  try {
    setIsLoading(true);
    const location = { lat: 40.7127281, lng: -74.0060152 };
    mapRef.current?.setCenter(location.lat, location.lng);
    mapRef.current?.setZoom(9);
    const bounds = mapRef.current?.getCurrentBounds();
    setCurrentCoordinates({ latitude: location.lat, longitude: location.lng });
 
    const searchResults = await searchDealers('', { latitude: location.lat, longitude: location.lng, bounds: bounds }, 'distance');
    if (searchResults) {
      setDealers(searchResults);
      setSelectedDealerId(null);
      hasInitialSearchRef.current = true;
    }
  } catch (error) {
    console.error('Error searching installers:', error);
  } finally {
    setIsLoading(false);
  }
}


export default FindDealer; 
